/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {GridFilterModel, GridRowGroupingModel} from '@mui/x-data-grid-premium';

export interface SavedSuperTableFilter {
  name: string;
  tableId: string;
  userId: string;
  criteria: GridFilterModel;
  groupingModel: GridRowGroupingModel;
}

const STORAGE_PREFIX = 'superTable';

function getStorageKey(userId: string, tableId: string): string {
  return `${STORAGE_PREFIX}_${userId}_${tableId}`;
}

export const SuperTableFilters = {
  async loadFilters(userId: string, tableId: string): Promise<SavedSuperTableFilter[]> {
    return new Promise((resolve) => {
      try {
        const key = getStorageKey(userId, tableId);
        const data = localStorage.getItem(key);
        const filters = data ? (JSON.parse(data) as SavedSuperTableFilter[]) : [];
        resolve(filters);
      } catch (error) {
        console.error('Error loading filters', error);
        resolve([]);
      }
    });
  },

  async saveFilter(filter: SavedSuperTableFilter): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const key = getStorageKey(filter.userId, filter.tableId);
        const existingData = localStorage.getItem(key);
        const filters: SavedSuperTableFilter[] = existingData ? JSON.parse(existingData) : [];
        if (filters.find((f) => f.name === filter.name)) {
          return reject(new Error('Filter name already exists. Please choose a different name.'));
        }
        filters.push(filter);
        localStorage.setItem(key, JSON.stringify(filters));
        resolve();
      } catch (error) {
        console.error('Error saving filter', error);
        reject(error);
      }
    });
  },

  async deleteFilter(userId: string, tableId: string, filterName: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const key = getStorageKey(userId, tableId);
        const existingData = localStorage.getItem(key);
        let filters: SavedSuperTableFilter[] = existingData ? JSON.parse(existingData) : [];
        filters = filters.filter((f) => f.name !== filterName);
        localStorage.setItem(key, JSON.stringify(filters));
        resolve();
      } catch (error) {
        console.error('Error deleting filter', error);
        reject(error);
      }
    });
  },
};
